import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useLanguage } from '../../i18n/LanguageContext';
import { Service, ServiceProvider, createServiceRequest } from '../../utils/servicesAPI';
import Button from './Button';
import Spinner from './Spinner';

// Styled components
const FormContainer = styled.div`
  background: linear-gradient(135deg, #111 0%, #1a1a1a 100%);
  border: 1px solid #333;
  border-radius: 12px;
  padding: 2rem;
  margin-top: 2rem;
`;

const FormTitle = styled.h3`
  font-size: 1.5rem;
  color: #0d9488;
  margin-bottom: 1.5rem;
  text-align: center;
`;

const FormSection = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  font-weight: 600;
  color: #fff;
  margin-bottom: 0.5rem;
  font-size: 0.95rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem 1rem;
  border: 2px solid #333;
  background: #000;
  color: #fff;
  border-radius: 8px;
  font-size: 1rem;
  transition: all 0.3s ease;
  box-sizing: border-box;

  &:focus {
    border-color: #0d9488;
    outline: none;
    box-shadow: 0 0 0 3px rgba(13, 148, 136, 0.1);
  }

  &::placeholder {
    color: #666;
  }

  &:disabled {
    background-color: #111;
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 100px;
  padding: 0.75rem 1rem;
  border: 2px solid #333;
  background: #000;
  color: #fff;
  border-radius: 8px;
  font-size: 1rem;
  font-family: inherit;
  resize: vertical;
  transition: all 0.3s ease;
  box-sizing: border-box;

  &:focus {
    border-color: #0d9488;
    outline: none;
    box-shadow: 0 0 0 3px rgba(13, 148, 136, 0.1);
  }

  &::placeholder {
    color: #666;
  }
`;

const DateTimeGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const TimeSlotContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.5rem;
`;

const TimeSlotButton = styled.button<{ $selected?: boolean }>`
  padding: 0.5rem 1rem;
  border: 2px solid ${props => props.$selected ? '#0d9488' : '#333'};
  background: ${props => props.$selected ? '#0d9488' : 'transparent'};
  color: ${props => props.$selected ? '#fff' : '#0d9488'};
  border-radius: 20px;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    border-color: #0d9488;
    background: ${props => props.$selected ? '#0d9488' : 'rgba(13, 148, 136, 0.1)'};
  }
`;

const ServiceInfo = styled.div`
  background: rgba(13, 148, 136, 0.1);
  border: 1px solid #0d9488;
  border-radius: 8px;
  padding: 1.5rem;
  margin-bottom: 2rem;
`;

const ServiceInfoTitle = styled.h4`
  color: #0d9488;
  margin-bottom: 1rem;
  font-size: 1.1rem;
`;

const ServiceDetail = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.5rem;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const ServiceDetailLabel = styled.span`
  color: #ccc;
`;

const ServiceDetailValue = styled.span`
  color: #fff;
  font-weight: 500;
`;

const WhatsAppContainer = styled.div`
  background: rgba(37, 211, 102, 0.1);
  border: 1px solid #25d366;
  border-radius: 8px;
  padding: 1rem;
  margin: 1.5rem 0;
  text-align: center;
`;

const WhatsAppButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: #25d366;
  color: white;
  padding: 0.75rem 1.5rem;
  border-radius: 25px;
  text-decoration: none;
  font-weight: 500;
  transition: all 0.3s ease;
  
  &:hover {
    background: #20c75b;
    transform: translateY(-2px);
  }
`;

const ErrorMessage = styled.div`
  color: #ef4444;
  font-size: 0.875rem;
  margin-top: 0.5rem;
`;

const SuccessMessage = styled.div`
  background: rgba(34, 197, 94, 0.1);
  border: 1px solid #22c55e;
  color: #22c55e;
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 1rem;
  text-align: center;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 2rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

interface ServiceRequestFormProps {
  service: Service;
  provider: ServiceProvider;
  onClose?: () => void;
}

const ServiceRequestForm: React.FC<ServiceRequestFormProps> = ({ 
  service, 
  provider,
  onClose 
}) => {
  const { language } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const [formData, setFormData] = useState({
    clientName: '',
    clientEmail: '',
    clientPhone: '',
    requestedDate: '',
    requestedTime: '',
    preferredTimeSlots: [] as string[],
    participantsCount: 1,
    message: ''
  });

  const timeSlots = [
    { value: 'morning', label: language === 'it' ? 'Mattina (9-12)' : 'Morning (9-12)' },
    { value: 'afternoon', label: language === 'it' ? 'Pomeriggio (14-17)' : 'Afternoon (14-17)' },
    { value: 'evening', label: language === 'it' ? 'Sera (18-21)' : 'Evening (18-21)' }
  ];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleTimeSlotToggle = (slot: string) => {
    setFormData(prev => ({
      ...prev,
      preferredTimeSlots: prev.preferredTimeSlots.includes(slot)
        ? prev.preferredTimeSlots.filter(s => s !== slot)
        : [...prev.preferredTimeSlots, slot]
    }));
  };

  const validateForm = (): string | null => {
    if (!formData.clientName.trim()) {
      return language === 'it' ? 'Il nome è obbligatorio' : 'Name is required';
    }
    if (!formData.clientEmail.trim()) {
      return language === 'it' ? "L'email è obbligatoria" : 'Email is required';
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.clientEmail)) {
      return language === 'it' ? 'Email non valida' : 'Invalid email';
    }
    if (formData.participantsCount < 1 || formData.participantsCount > (service.max_participants || 10)) {
      return language === 'it' 
        ? `Il numero di partecipanti deve essere tra 1 e ${service.max_participants || 10}` 
        : `Number of participants must be between 1 and ${service.max_participants || 10}`;
    }
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await createServiceRequest({
        serviceId: service.id,
        clientName: formData.clientName,
        clientEmail: formData.clientEmail,
        clientPhone: formData.clientPhone,
        requestedDate: formData.requestedDate,
        requestedTime: formData.requestedTime,
        preferredTimeSlots: formData.preferredTimeSlots,
        participantsCount: formData.participantsCount,
        message: formData.message
      });

      if (result.success) {
        setSuccess(true);
        // Reset form dopo successo
        setTimeout(() => {
          onClose?.();
        }, 3000);
      } else {
        setError(result.error || 'Errore durante l\'invio della richiesta');
      }
    } catch (err) {
      setError('Errore durante l\'invio della richiesta');
    } finally {
      setLoading(false);
    }
  };

  const formatDuration = (minutes?: number): string => {
    if (!minutes) return '';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    
    if (hours === 0) return `${mins} min`;
    if (mins === 0) return `${hours}h`;
    return `${hours}h ${mins}min`;
  };

  const formatPrice = (): string => {
    if (service.price_per_person) {
      return `€${service.price_per_person}/${language === 'it' ? 'persona' : 'person'}`;
    }
    if (service.price_per_group) {
      return `€${service.price_per_group}/${language === 'it' ? 'gruppo' : 'group'}`;
    }
    return language === 'it' ? 'Su richiesta' : 'On request';
  };

  const getWhatsAppMessage = (): string => {
    const message = language === 'it'
      ? `Ciao ${provider.name}! Sono interessato al servizio "${service.title_it}". Potresti contattarmi per maggiori informazioni?`
      : `Hi ${provider.name}! I'm interested in the "${service.title_en}" service. Could you contact me for more information?`;
    
    return encodeURIComponent(message);
  };

  if (success) {
    return (
      <FormContainer>
        <SuccessMessage>
          <h4>✅ {language === 'it' ? 'Richiesta inviata!' : 'Request sent!'}</h4>
          <p>
            {language === 'it' 
              ? 'Grazie per la tua richiesta. Ti contatteremo entro 24 ore per confermare i dettagli.'
              : 'Thank you for your request. We will contact you within 24 hours to confirm the details.'}
          </p>
          {provider.whatsapp && (
            <p style={{ marginTop: '1rem' }}>
              <WhatsAppButton
                href={`https://wa.me/${provider.whatsapp.replace('+', '')}?text=${getWhatsAppMessage()}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                📱 {language === 'it' ? 'Contatta via WhatsApp' : 'Contact via WhatsApp'}
              </WhatsAppButton>
            </p>
          )}
        </SuccessMessage>
      </FormContainer>
    );
  }

  return (
    <FormContainer>
      <FormTitle>
        {language === 'it' ? 'Richiedi Informazioni' : 'Request Information'}
      </FormTitle>

      {/* Info servizio */}
      <ServiceInfo>
        <ServiceInfoTitle>
          {language === 'it' ? service.title_it : service.title_en}
        </ServiceInfoTitle>
        <ServiceDetail>
          <ServiceDetailLabel>Provider:</ServiceDetailLabel>
          <ServiceDetailValue>{provider.name}</ServiceDetailValue>
        </ServiceDetail>
        {service.duration_minutes && (
          <ServiceDetail>
            <ServiceDetailLabel>
              {language === 'it' ? 'Durata:' : 'Duration:'}
            </ServiceDetailLabel>
            <ServiceDetailValue>{formatDuration(service.duration_minutes)}</ServiceDetailValue>
          </ServiceDetail>
        )}
        <ServiceDetail>
          <ServiceDetailLabel>
            {language === 'it' ? 'Max partecipanti:' : 'Max participants:'}
          </ServiceDetailLabel>
          <ServiceDetailValue>{service.max_participants}</ServiceDetailValue>
        </ServiceDetail>
        <ServiceDetail>
          <ServiceDetailLabel>
            {language === 'it' ? 'Prezzo:' : 'Price:'}
          </ServiceDetailLabel>
          <ServiceDetailValue>{formatPrice()}</ServiceDetailValue>
        </ServiceDetail>
      </ServiceInfo>

      {/* WhatsApp rapido */}
      {provider.whatsapp && (
        <WhatsAppContainer>
          <h4 style={{ color: '#25d366', marginBottom: '0.5rem' }}>
            {language === 'it' ? '⚡ Contatto Rapido' : '⚡ Quick Contact'}
          </h4>
          <p style={{ color: '#ccc', fontSize: '0.9rem', marginBottom: '1rem' }}>
            {language === 'it' 
              ? 'Per domande urgenti, contatta direttamente su WhatsApp'
              : 'For urgent questions, contact directly via WhatsApp'}
          </p>
          <WhatsAppButton
            href={`https://wa.me/${provider.whatsapp.replace('+', '')}?text=${getWhatsAppMessage()}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            📱 WhatsApp {provider.name}
          </WhatsAppButton>
        </WhatsAppContainer>
      )}

      {/* Form richiesta */}
      <form onSubmit={handleSubmit}>
        {/* Dati personali */}
        <FormSection>
          <Label htmlFor="clientName">
            {language === 'it' ? 'Nome e Cognome *' : 'Full Name *'}
          </Label>
          <Input
            id="clientName"
            name="clientName"
            type="text"
            value={formData.clientName}
            onChange={handleInputChange}
            placeholder={language === 'it' ? 'Il tuo nome completo' : 'Your full name'}
            disabled={loading}
            required
          />
        </FormSection>

        <DateTimeGrid>
          <FormSection>
            <Label htmlFor="clientEmail">
              {language === 'it' ? 'Email *' : 'Email *'}
            </Label>
            <Input
              id="clientEmail"
              name="clientEmail"
              type="email"
              value={formData.clientEmail}
              onChange={handleInputChange}
              placeholder="email@example.com"
              disabled={loading}
              required
            />
          </FormSection>

          <FormSection>
            <Label htmlFor="clientPhone">
              {language === 'it' ? 'Telefono' : 'Phone'}
            </Label>
            <Input
              id="clientPhone"
              name="clientPhone"
              type="tel"
              value={formData.clientPhone}
              onChange={handleInputChange}
              placeholder="+39 333 123 4567"
              disabled={loading}
            />
          </FormSection>
        </DateTimeGrid>

        {/* Preferenze data/ora */}
        <DateTimeGrid>
          <FormSection>
            <Label htmlFor="requestedDate">
              {language === 'it' ? 'Data preferita' : 'Preferred date'}
            </Label>
            <Input
              id="requestedDate"
              name="requestedDate"
              type="date"
              value={formData.requestedDate}
              onChange={handleInputChange}
              min={new Date().toISOString().split('T')[0]}
              disabled={loading}
            />
          </FormSection>

          <FormSection>
            <Label htmlFor="requestedTime">
              {language === 'it' ? 'Orario preferito' : 'Preferred time'}
            </Label>
            <Input
              id="requestedTime"
              name="requestedTime"
              type="time"
              value={formData.requestedTime}
              onChange={handleInputChange}
              disabled={loading}
            />
          </FormSection>
        </DateTimeGrid>

        {/* Fasce orarie */}
        <FormSection>
          <Label>
            {language === 'it' ? 'Fasce orarie disponibili' : 'Available time slots'}
          </Label>
          <TimeSlotContainer>
            {timeSlots.map(slot => (
              <TimeSlotButton
                key={slot.value}
                type="button"
                $selected={formData.preferredTimeSlots.includes(slot.value)}
                onClick={() => handleTimeSlotToggle(slot.value)}
                disabled={loading}
              >
                {slot.label}
              </TimeSlotButton>
            ))}
          </TimeSlotContainer>
        </FormSection>

        {/* Numero partecipanti */}
        <FormSection>
          <Label htmlFor="participantsCount">
            {language === 'it' ? 'Numero partecipanti *' : 'Number of participants *'}
          </Label>
          <Input
            id="participantsCount"
            name="participantsCount"
            type="number"
            min="1"
            max={service.max_participants}
            value={formData.participantsCount}
            onChange={handleInputChange}
            disabled={loading}
            required
          />
        </FormSection>

        {/* Messaggio */}
        <FormSection>
          <Label htmlFor="message">
            {language === 'it' ? 'Messaggio aggiuntivo' : 'Additional message'}
          </Label>
          <TextArea
            id="message"
            name="message"
            value={formData.message}
            onChange={handleInputChange}
            placeholder={language === 'it' 
              ? 'Descrivi le tue esigenze particolari o fai domande specifiche...'
              : 'Describe your particular needs or ask specific questions...'}
            disabled={loading}
          />
        </FormSection>

        {error && <ErrorMessage>{error}</ErrorMessage>}

        <ButtonGroup>
          <Button
            type="submit"
            variant="primary"
            disabled={loading}
            style={{ flex: 1 }}
          >
            {loading ? (
              <>
                <Spinner />
                {language === 'it' ? 'Invio in corso...' : 'Sending...'}
              </>
            ) : (
              <>
                📩 {language === 'it' ? 'Invia Richiesta' : 'Send Request'}
              </>
            )}
          </Button>
          
          {onClose && (
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={loading}
            >
              {language === 'it' ? 'Annulla' : 'Cancel'}
            </Button>
          )}
        </ButtonGroup>
      </form>
    </FormContainer>
  );
};

export default ServiceRequestForm; 